import { Add, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import PrimaryButton from "src/componentsV2/buttons/PrimaryButton";
import SecondaryButton from "src/componentsV2/buttons/SecondaryButton";
import { useUrlSearch } from "../../components/urlSearch";
import AvailabilityPicker from "../../componentsV2/AvailabilityPicker";
import { Loading } from "../../componentsV2/Loading";
import { OneClickBackground } from "../../componentsV2/OneClickBackground";
import OneClickLogoImage from "../../componentsV2/OneClickLogoImage";
import useGeneralNotifications from "../../hooks/useGeneralNotifications";
import {
  useGuestAccept,
  useGuestDecline,
  useGuestProposeNewTime,
} from "../../mutations";
import {
  useHostAvailability,
  useValidateGuestDeclineAction,
} from "../../queries";
import { useValidateGuestAction } from "../../queries/useValidateGuestAction";
import { GuestActionSource, GuestAction as GuestActionType } from "../../types";
import { useLogoUrl } from "../../utils/logoUrl";
import InvalidUrl from "../InvalidUrl";
import BorderBox from "../../componentsV2/BorderBox";
import { InviteStyle, isWebinar } from "../../meetingTypes/invite/props";
import { useTurnstile } from "../../hooks/useTurnstile";

const MANUAL_ACCEPT_CONFIRMATION_THRESHOLD_SECONDS = 1800;

function Accept({
  orgId,
  meetingId,
  contactId,
  secondsSinceLastEmailSent,
}: {
  orgId: string;
  meetingId: string;
  contactId: string;
  secondsSinceLastEmailSent: number | undefined;
}) {
  const history = useHistory();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const acceptedRef = useRef(false);

  const guestAccept = useGuestAccept();
  const {
    token,
    isReady,
    error: turnstileLoadError,
    containerRef,
  } = useTurnstile();

  const turnstileError =
    turnstileLoadError || !window.config?.TURNSTILE_SITE_KEY;

  const isTooEarly =
    secondsSinceLastEmailSent !== undefined &&
    secondsSinceLastEmailSent < MANUAL_ACCEPT_CONFIRMATION_THRESHOLD_SECONDS;

  const doAccept = useCallback(
    (turnstileToken?: string | null) => {
      if (acceptedRef.current) return;
      acceptedRef.current = true;
      setLoading(true);
      guestAccept(contactId, meetingId, orgId, turnstileToken)
        .then(() => {
          setAccepted(true);
        })
        .catch((err) => {
          acceptedRef.current = false;
          if (err?.status === 409) {
            history.push("/pending");
          } else {
            history.push("/error");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [guestAccept, contactId, meetingId, orgId, history],
  );

  // Auto-accept once Turnstile produces a token — only when not accepted too early
  useEffect(() => {
    if (!isTooEarly && !turnstileError && isReady && token) {
      doAccept(token);
    }
  }, [isTooEarly, turnstileError, isReady, token, doAccept]);

  const buttonDisabled = !isReady || turnstileError;

  if (loading) {
    return <Loading />;
  }

  if (accepted) {
    return <Redirect to="/accepted" />;
  }

  return (
    <Container maxWidth="xs">
      <Stack
        alignItems="center"
        spacing={2}
        sx={{ minHeight: "100vh", paddingTop: theme.spacing(4) }}
      >
        <BorderBox>
          <Stack alignItems="center" spacing={2} sx={{ padding: 3 }}>
            <InfoOutlined
              sx={{
                color: theme.palette.primary.main,
                height: "5em",
                width: "5em",
              }}
            />
            {turnstileError ? (
              <>
                <Typography variant="h6">Verification unavailable</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  We are unable to verify your request at this time. Please try
                  again later or contact the meeting organizer.
                </Typography>
              </>
            ) : isTooEarly ? (
              <>
                <Typography variant="h6">
                  Human Verification Required
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  To ensure the security and integrity of our platform, we
                  occasionally require verification for certain actions. Please
                  click the button below to confirm you are human. Thank you!
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6">Verifying...</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Please wait while we verify your request.
                </Typography>
              </>
            )}
            <div ref={containerRef} />
            {isTooEarly && !turnstileError && (
              <PrimaryButton
                onClick={() => doAccept(token)}
                disabled={buttonDisabled}
              >
                Confirm
              </PrimaryButton>
            )}
          </Stack>
        </BorderBox>
      </Stack>
    </Container>
  );
}

function Decline({
  orgId,
  meetingId,
  meetingInviteStyle,
  contactId,
}: {
  orgId: string;
  meetingId: string;
  meetingInviteStyle: InviteStyle | undefined;
  contactId: string;
}) {
  const { search, pathname } = useLocation();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const theme = useTheme();

  const guestDecline = useGuestDecline();
  const {
    token: turnstileToken,
    isReady,
    error: turnstileLoadError,
    containerRef,
  } = useTurnstile();

  const turnstileError =
    turnstileLoadError || !window.config?.TURNSTILE_SITE_KEY;
  const buttonsDisabled = !isReady || turnstileError;

  const { error } = useValidateGuestDeclineAction(orgId, meetingId, contactId);
  if (error) {
    return <Redirect to="/error" />;
  }

  const handleConfirmationClick = () => {
    setLoading(true);
    setConfirmed(true);
    guestDecline(contactId, meetingId, orgId, turnstileToken)
      .catch((err) => {
        setConfirmed(false);
        if (err?.status === 409) {
          history.push("/pending");
        } else {
          history.push("/error");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleProposeTime = () => {
    const query = new URLSearchParams(search);
    query.set("a", "propose_new_time");
    const url = `${pathname}?${query.toString()}`;

    history.push(url);
  };

  if (loading) {
    return <Loading />;
  }

  if (confirmed) {
    return <Redirect to="/declined" />;
  }

  return (
    <Container maxWidth="xs">
      <Stack
        alignItems="center"
        spacing={2}
        sx={{ minHeight: "100vh", paddingTop: theme.spacing(8) }}
      >
        <Typography>Are you sure you want to decline this meeting?</Typography>
        <div ref={containerRef} />
        {turnstileError && (
          <Typography variant="body2" color="error" align="center">
            Verification unavailable. Please try again later or contact the
            meeting organizer.
          </Typography>
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <SecondaryButton
            nowrap
            onClick={handleConfirmationClick}
            disabled={buttonsDisabled}
          >
            Decline
          </SecondaryButton>
          {!isWebinar(meetingInviteStyle) && (
            <PrimaryButton
              icon={<Add />}
              nowrap
              onClick={handleProposeTime}
              disabled={buttonsDisabled}
            >
              Propose Time
            </PrimaryButton>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}

function ProposeNewTime({
  orgId,
  meetingId,
  contactId,
  source,
}: {
  orgId: string;
  meetingId: string;
  contactId: string;
  source: GuestActionSource | null;
}) {
  const theme = useTheme();

  const sm = useMediaQuery(theme.breakpoints.up("md"));

  const [proposedTime, setProposedTime] = useState<dayjs.Dayjs | null>(null);
  const [selection, setSelection] = useState<dayjs.Dayjs | null>(null);
  const [userSelected, setUserSelected] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const url = useLocation<{ url: string }>().state?.url;

  const { addError } = useGeneralNotifications();

  const guestProposeNewTime = useGuestProposeNewTime();

  const { data, loading, error } = useHostAvailability(
    orgId,
    meetingId,
    contactId,
  );

  const handleMeetingGuestAction = () => {
    guestProposeNewTime(
      contactId,
      meetingId,
      orgId,
      proposedTime as Dayjs,
      source,
    )
      .then(() => {
        setConfirmed(true);
      })
      .catch((error: { message: string }) => {
        addError(`could not submit time proposal: ${error.message}`);
      });
  };

  useEffect(() => {
    if (!data?.availableTimes?.[0]) {
      return;
    }

    const firstAvailableTime = dayjs(data.availableTimes[0]);
    setProposedTime(firstAvailableTime);
  }, [data]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    switch (error.status) {
      case 409:
        if (error.response?.body?.message) {
          return <Redirect to="/pending" />;
        }
        return <Redirect to="/error" />;
      case 400:
        return <InvalidUrl />;
      default:
        return <Redirect to="/error" />;
    }
  }

  if (!data) {
    return <Redirect to="/error" />;
  }

  if (confirmed) {
    return <Redirect to="/accepted" />;
  }

  if (data.availableTimes.length === 0) {
    return (
      <Container maxWidth="sm">
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "100vh" }}
        >
          <Typography variant="h4" align="center">
            The meeting host has no availability at this time
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <OneClickBackground>
      <Stack alignItems="center" sx={{ marginTop: theme.spacing(4) }}>
        <Paper
          elevation={0}
          sx={{ maxWidth: "fit-content", padding: theme.spacing(4) }}
        >
          <Stack alignItems="center">
            <Box sx={{ marginBottom: "25px" }}>
              {url ? (
                <a href={url}>
                  <OneClickLogoImage />
                </a>
              ) : (
                <OneClickLogoImage />
              )}
            </Box>
          </Stack>
          <Typography variant="h5">Select a date and time</Typography>
          <AvailabilityPicker
            variant={sm ? "horizontal" : "vertical"}
            date={proposedTime}
            selection={selection}
            availableSlots={data.availableTimes.map((d) => dayjs(d)) || []}
            onDateChange={setProposedTime}
            onSelectionChange={(dt) => {
              setSelection(dt);
              setProposedTime(dt);
              setUserSelected(true);
            }}
          />
          <Stack
            direction="row"
            justifyContent="flex-end"
            paddingTop={theme.spacing(2)}
          >
            {/*Disable the button if the user has not made a selection (userSelected is false) or if the selected value is null (selection == null).*/}
            <PrimaryButton
              onClick={handleMeetingGuestAction}
              disabled={!userSelected || selection == null}
            >
              Confirm
            </PrimaryButton>
          </Stack>
        </Paper>
      </Stack>
    </OneClickBackground>
  );
}

export default function Page() {
  document.title = "Processing Meeting Guest Action";

  const query = useUrlSearch();

  // Get the Org's UUID
  const org = query.get("o");
  // Get the Meeting's UUID
  const meeting = query.get("m");
  // Get the Contact's UUID
  const contact = query.get("c");
  // Get the Contact's Action
  const action = query.get("a") as GuestActionType | null;
  // Get the Contact's Action Source
  const actionSource = query.get("s") as GuestActionSource | null;

  const isValid =
    org != null &&
    meeting != null &&
    contact != null &&
    action != null &&
    ["propose_new_time", "accept", "decline"].includes(action);

  const {
    data: validationData,
    loading,
    error,
  } = useValidateGuestAction(
    isValid ? action! : "accept",
    org ?? "",
    meeting ?? "",
    contact ?? "",
    isValid,
  );

  const [, setLogoUrl] = useLogoUrl();
  useEffect(() => {
    if (validationData?.logoURL) {
      setLogoUrl(validationData.logoURL);
    }
  }, [validationData?.logoURL]);

  if (!isValid) {
    return <InvalidUrl />;
  }

  if (loading) {
    return null;
  }

  if (error) {
    return <InvalidUrl />;
  }

  switch (action) {
    case "accept":
      return (
        <Accept
          orgId={org}
          meetingId={meeting}
          contactId={contact}
          secondsSinceLastEmailSent={validationData?.secondsSinceLastEmailSent}
        />
      );
    case "decline":
      return (
        <Decline
          orgId={org}
          meetingId={meeting}
          meetingInviteStyle={validationData?.inviteStyle}
          contactId={contact}
        />
      );
    case "propose_new_time":
      return (
        <ProposeNewTime
          orgId={org}
          meetingId={meeting}
          contactId={contact}
          source={actionSource}
        />
      );
    default:
      return <InvalidUrl />;
  }
}
